import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiCall } from '../utils/api';
import CameraRecorder, { CameraRecorderHandle, RecordingMeta } from '../components/CameraRecorder';
import { uploadPerformanceVideo } from '../utils/uploadPerformanceVideo';
import { savePending, listPending, removePending, PendingUpload } from '../utils/pendingUploads';

interface QueueItem {
  id: number;
  song_id: number;
  user_name: string;
  queue_order: number;
  status: string;
  added_at: string;
  title: string;
  artist: string;
  genre: string;
  pdf_path: string;
  video_consent: boolean;
}

interface PlayedSong {
  history_id: number;
  song_id: number;
  user_name: string;
  status: string;
  completed_at: string | null;
  title: string;
  artist: string;
  average_rating: number;
  vote_count: number;
  video_consent: boolean;
  video_status: string | null;
  video_emailed_at: string | null;
}

interface Gig {
  id: number;
  name: string;
  started_at: string;
  ended_at: string | null;
  status: 'upcoming' | 'active' | 'ended';
  notes: string | null;
  scheduled_date: string | null;
  venue: string | null;
  queue_count?: string;
  video_enabled?: boolean;
}

function renderVideoCell(song: PlayedSong) {
  if (!song.video_consent) {
    return <span style={{ color: '#5a3a1a' }}>—</span>;
  }
  const map: Record<string, { label: string; color: string; bg: string }> = {
    ready: { label: song.video_emailed_at ? 'Emailed' : 'Ready', color: '#2ecc71', bg: 'rgba(46,204,113,0.12)' },
    uploading: { label: 'Uploading', color: '#c8850f', bg: 'rgba(200,133,15,0.12)' },
    failed: { label: 'Failed', color: '#e74c3c', bg: 'rgba(231,76,60,0.12)' },
  };
  const entry = song.video_status ? map[song.video_status] : null;
  if (!entry) {
    return (
      <span title="Performer opted in but no recording was captured" style={{ color: '#e74c3c', background: 'rgba(231,76,60,0.12)', padding: '2px 8px', borderRadius: 10, fontWeight: 'bold', letterSpacing: 1 }}>
        Missed
      </span>
    );
  }
  return (
    <span title={`video_status: ${song.video_status}${song.video_emailed_at ? `; emailed ${new Date(song.video_emailed_at).toLocaleString()}` : ''}`} style={{ color: entry.color, background: entry.bg, padding: '2px 8px', borderRadius: 10, fontWeight: 'bold', letterSpacing: 1 }}>
      {entry.label}
    </span>
  );
}

const QueueAdminPage: React.FC = () => {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [playedSongs, setPlayedSongs] = useState<PlayedSong[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  // Auto-dismiss messages after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Gig state
  const [currentGig, setCurrentGig] = useState<Gig | null>(null);
  const [upcomingGigs, setUpcomingGigs] = useState<Gig[]>([]);
  const [showStartGigDialog, setShowStartGigDialog] = useState(false);
  const [showEndGigDialog, setShowEndGigDialog] = useState(false);
  const [showCreateGigForm, setShowCreateGigForm] = useState(false);
  const [newGigName, setNewGigName] = useState('');
  const [newGigDate, setNewGigDate] = useState('');
  const [newGigVenue, setNewGigVenue] = useState('');
  const [newGigVideoEnabled, setNewGigVideoEnabled] = useState(true);

  // Drag state
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // Camera recorder
  const recorderRef = useRef<CameraRecorderHandle>(null);
  const videoRecordingEnabled = !!currentGig?.video_enabled;
  const [cameraOn, setCameraOn] = useState(false);
  const [recording, setRecording] = useState(false);

  // Pending uploads queue (persists across reloads via IndexedDB)
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const refreshPending = useCallback(async () => {
    try {
      const items = await listPending();
      setPendingUploads(items);
    } catch (err) {
      console.warn('Failed to read pending uploads:', err);
    }
  }, []);
  useEffect(() => { refreshPending(); }, [refreshPending]);

  const attemptUpload = useCallback(async (pending: PendingUpload): Promise<boolean> => {
    try {
      await uploadPerformanceVideo({
        historyId: pending.historyId,
        blob: pending.blob,
        extension: pending.extension,
        contentType: pending.contentType,
      });
      await removePending(pending.historyId);
      await refreshPending();
      return true;
    } catch (err: any) {
      console.error(`Upload failed for history ${pending.historyId}:`, err);
      pending.attempts += 1;
      pending.lastError = err?.message || 'unknown error';
      await savePending(pending);
      await refreshPending();
      // Best-effort: tell the server so the row reflects failure state.
      apiCall(`/api/songs/history/${pending.historyId}/video/failed`, { method: 'POST' }).catch(() => {});
      return false;
    }
  }, [refreshPending]);

  const handleFinishedRecording = useCallback(async (result: { blob: Blob; meta: RecordingMeta; mimeType: string }) => {
    const extension: 'mp4' | 'webm' = result.mimeType.includes('mp4') ? 'mp4' : 'webm';
    const pending: PendingUpload = {
      historyId: result.meta.historyId,
      songTitle: result.meta.songTitle,
      artist: result.meta.artist,
      userName: result.meta.userName,
      extension,
      contentType: result.mimeType,
      blob: result.blob,
      createdAt: Date.now(),
      attempts: 0,
    };
    // Persist first so a tab close mid-upload doesn't lose the take
    await savePending(pending);
    await refreshPending();
    setMessage(`Uploading ${Math.round(pending.blob.size / 1024 / 1024)} MB recording for ${pending.userName}...`);
    const ok = await attemptUpload(pending);
    if (ok) {
      setMessage(`Video uploaded for ${pending.userName} — ${pending.songTitle}.`);
    } else {
      setMessage(`Upload failed for ${pending.userName} — saved locally, retry from the Pending uploads panel.`);
    }
  }, [attemptUpload, refreshPending]);

  useEffect(() => {
    fetchCurrentGig();
    fetchUpcomingGigs();
    fetchQueue();
    fetchPlayedSongs();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchCurrentGig();
      fetchUpcomingGigs();
      if (!hasLocalChanges) {
        fetchQueue();
      }
      fetchPlayedSongs();
    }, 5000);
    return () => clearInterval(interval);
  }, [hasLocalChanges]);

  const fetchCurrentGig = async () => {
    try {
      const res = await apiCall('/api/songs/gigs/current');
      if (res.ok) {
        const data = await res.json();
        setCurrentGig(data);
      }
    } catch (err) {
      // Silently fail
    }
  };

  const fetchUpcomingGigs = async () => {
    try {
      const res = await apiCall('/api/songs/gigs/upcoming');
      if (res.ok) {
        const data = await res.json();
        setUpcomingGigs(data);
      }
    } catch (err) {
      // Silently fail
    }
  };

  const toggleVideoEnabled = async (enabled: boolean) => {
    if (!currentGig) return;
    setMessage(null);
    const previous = currentGig.video_enabled;
    setCurrentGig({ ...currentGig, video_enabled: enabled });
    try {
      const res = await apiCall(`/api/songs/gigs/${currentGig.id}/video-enabled`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_enabled: enabled }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to update video setting');
      }
      setMessage(enabled ? 'Video recording enabled for this gig' : 'Video recording disabled for this gig');
    } catch (err: any) {
      setCurrentGig({ ...currentGig, video_enabled: previous });
      setMessage(err.message || 'Failed to update video setting');
    }
  };

  const createUpcomingGig = async () => {
    if (!newGigName.trim() || !newGigDate) {
      setMessage('Gig name and date are required');
      return;
    }
    setReordering(true);
    setMessage(null);
    try {
      const res = await apiCall('/api/songs/gigs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newGigName.trim(),
          scheduled_date: newGigDate,
          venue: newGigVenue.trim() || null,
          video_enabled: newGigVideoEnabled,
        })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to create gig');
      }
      const gig = await res.json();
      setMessage(`Upcoming gig "${gig.name}" created!`);
      setShowCreateGigForm(false);
      setNewGigName('');
      setNewGigDate('');
      setNewGigVenue('');
      setNewGigVideoEnabled(true);
      fetchUpcomingGigs();
    } catch (err: any) {
      setMessage(err.message || 'Error creating gig');
    } finally {
      setReordering(false);
    }
  };

  const goLiveWithGig = async (gigId: number) => {
    setReordering(true);
    setMessage(null);
    try {
      const res = await apiCall('/api/songs/gigs/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gig_id: gigId })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to go live');
      }
      const gig = await res.json();
      setCurrentGig(gig);
      setMessage(`Gig "${gig.name}" is now live!`);
      fetchUpcomingGigs();
      fetchQueue();
    } catch (err: any) {
      setMessage(err.message || 'Error going live');
    } finally {
      setReordering(false);
    }
  };

  const deleteUpcomingGig = async (gigId: number) => {
    setReordering(true);
    setMessage(null);
    try {
      const res = await apiCall(`/api/songs/gigs/${gigId}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete gig');
      }
      setMessage('Gig deleted');
      fetchUpcomingGigs();
    } catch (err: any) {
      setMessage(err.message || 'Error deleting gig');
    } finally {
      setReordering(false);
    }
  };

  const startGig = async () => {
    if (!newGigName.trim()) {
      setMessage('Please enter a gig name');
      return;
    }
    setReordering(true);
    setMessage(null);
    try {
      const res = await apiCall('/api/songs/gigs/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newGigName.trim() })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to start gig');
      }
      const gig = await res.json();
      setCurrentGig(gig);
      setMessage(`Gig "${gig.name}" started!`);
      setShowStartGigDialog(false);
      setNewGigName('');
      fetchQueue();
    } catch (err: any) {
      setMessage(err.message || 'Error starting gig');
    } finally {
      setReordering(false);
    }
  };

  const endGig = async () => {
    setReordering(true);
    setMessage(null);
    try {
      const res = await apiCall('/api/songs/gigs/end', {
        method: 'POST'
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to end gig');
      }
      const gig = await res.json();
      setMessage(`Gig "${gig.name}" ended!`);
      setCurrentGig(null);
      setShowEndGigDialog(false);
      fetchQueue();
      fetchPlayedSongs();
    } catch (err: any) {
      setMessage(err.message || 'Error ending gig');
    } finally {
      setReordering(false);
    }
  };

  const fetchQueue = async () => {
    try {
      const res = await apiCall('/api/songs/queue');
      if (!res.ok) throw new Error('Failed to fetch queue');
      const data = await res.json();
      setQueue(data);
      setHasLocalChanges(false);
      setError(null);
    } catch (err: any) {
      // Only show error on initial load, not on polling failures
      if (loading) {
        setError(err.message || 'Unknown error');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayedSongs = async () => {
    try {
      const res = await apiCall('/api/songs/played?limit=20');
      if (res.ok) {
        const data = await res.json();
        setPlayedSongs(data);
      }
    } catch (err) {
      // Silently fail for played songs
    }
  };

  const activeQueue = queue.filter(item => item.status === 'queued' || item.status === 'playing');

  // Drag and drop handlers
  const handleDragStart = useCallback((idx: number) => {
    dragItem.current = idx;
    setDragIndex(idx);
  }, []);

  const handleDragEnter = useCallback((idx: number) => {
    dragOverItem.current = idx;
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragItem.current === null || dragOverItem.current === null) {
      setDragIndex(null);
      return;
    }
    if (dragItem.current === dragOverItem.current) {
      setDragIndex(null);
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const fromIdx = dragItem.current;
    const toIdx = dragOverItem.current;

    // Map active queue indices to full queue indices and reorder
    const activeIds = activeQueue.map(item => item.id);
    const movedId = activeIds[fromIdx];
    const newActiveIds = [...activeIds];
    newActiveIds.splice(fromIdx, 1);
    newActiveIds.splice(toIdx, 0, movedId);

    // Rebuild full queue in new order
    const activeSet = new Set(newActiveIds);
    const nonActive = queue.filter(item => !activeSet.has(item.id));
    const reorderedActive = newActiveIds.map(id => queue.find(item => item.id === id)!);
    setQueue([...reorderedActive, ...nonActive]);
    setHasLocalChanges(true);

    setDragIndex(null);
    dragItem.current = null;
    dragOverItem.current = null;
  }, [activeQueue, queue]);

  const moveItem = useCallback((fromIdx: number, toIdx: number) => {
    if (toIdx < 0 || toIdx >= activeQueue.length) return;
    const activeIds = activeQueue.map(item => item.id);
    const movedId = activeIds[fromIdx];
    const newActiveIds = [...activeIds];
    newActiveIds.splice(fromIdx, 1);
    newActiveIds.splice(toIdx, 0, movedId);
    const activeSet = new Set(newActiveIds);
    const nonActive = queue.filter(item => !activeSet.has(item.id));
    const reorderedActive = newActiveIds.map(id => queue.find(item => item.id === id)!);
    setQueue([...reorderedActive, ...nonActive]);
    setHasLocalChanges(true);
  }, [activeQueue, queue]);

  const saveOrder = async () => {
    setReordering(true);
    setMessage(null);
    try {
      const res = await apiCall('/api/songs/queue/reorder', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ queue: queue.map(item => item.id) })
      });
      if (!res.ok) throw new Error('Failed to reorder queue');
      setMessage('Queue order updated!');
      fetchQueue();
    } catch (err: any) {
      setMessage(err.message || 'Error reordering queue');
    } finally {
      setReordering(false);
    }
  };

  const removeItem = async (id: number) => {
    setMessage(null);
    try {
      const res = await apiCall(`/api/songs/queue/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove song from queue');
      setMessage('Song removed from queue');
      fetchQueue();
    } catch (err: any) {
      setMessage(err.message || 'Error removing song');
    }
  };

  const markAsComplete = async (id: number) => {
    setMessage(null);
    setReordering(true);
    try {
      const res = await apiCall(`/api/songs/queue/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'played' })
      });
      if (!res.ok) throw new Error('Failed to mark song as complete');
      setMessage('Song marked as complete!');
      fetchQueue();
      fetchPlayedSongs();
      if (recorderRef.current?.isRecording()) {
        const result = await recorderRef.current.stop();
        if (result) handleFinishedRecording(result);
      }
    } catch (err: any) {
      setMessage(err.message || 'Error marking song as complete');
    } finally {
      setReordering(false);
    }
  };

  const unstartSong = async (id: number) => {
    setMessage(null);
    setReordering(true);
    try {
      const res = await apiCall(`/api/songs/queue/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'queued' })
      });
      if (!res.ok) throw new Error('Failed to stop song');
      setMessage('Song stopped and returned to queue');
      fetchQueue();
      recorderRef.current?.cancel();
    } catch (err: any) {
      setMessage(err.message || 'Error stopping song');
    } finally {
      setReordering(false);
    }
  };

  const setCurrentSong = async (id: number) => {
    setMessage(null);
    setReordering(true);
    try {
      // Only un-start the currently playing song (if any), not all items
      const playingItem = queue.find(item => item.status === 'playing');
      if (playingItem && playingItem.id !== id) {
        const unRes = await apiCall(`/api/songs/queue/${playingItem.id}/status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'queued' })
        });
        if (!unRes.ok) throw new Error('Failed to stop current song');
      }

      // Move selected song to top of queue
      const idx = queue.findIndex(item => item.id === id);
      const newQueue = [...queue];
      if (idx >= 0) {
        const [selected] = newQueue.splice(idx, 1);
        newQueue.unshift(selected);

        const reorderRes = await apiCall('/api/songs/queue/reorder', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ queue: newQueue.map(item => item.id) })
        });
        if (!reorderRes.ok) throw new Error('Failed to reorder queue');
      }

      // Confirm before starting a consented song with the camera off — easy to miss otherwise
      const startedItem = queue.find(q => q.id === id);
      if (videoRecordingEnabled && startedItem?.video_consent && !cameraOn) {
        const proceed = window.confirm(
          `Camera is OFF.\n\n${startedItem.user_name} opted in to a video of "${startedItem.title}".\n\n` +
          `Click OK to start the song WITHOUT recording, or Cancel to turn the camera on first.`
        );
        if (!proceed) {
          setMessage('Cancelled — turn the camera on, then hit Start again.');
          return;
        }
      }

      // Set selected song to playing
      const statusRes = await apiCall(`/api/songs/queue/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'playing' })
      });
      if (!statusRes.ok) throw new Error('Failed to set song as playing');
      const statusBody = await statusRes.json() as { song_history_id: number | null };

      // Trigger camera recording for performers who opted in
      if (videoRecordingEnabled && startedItem?.video_consent && statusBody.song_history_id && recorderRef.current) {
        const ok = recorderRef.current.start({
          queueItemId: startedItem.id,
          historyId: statusBody.song_history_id,
          songTitle: startedItem.title,
          artist: startedItem.artist,
          userName: startedItem.user_name,
        });
        if (!ok) {
          setMessage('Started song, but camera could not begin recording (check camera is on).');
        }
      }

      setMessage('Current song updated and moved to top!');
      fetchQueue();
    } catch (err: any) {
      setMessage(err.message || 'Error updating current song');
    } finally {
      setReordering(false);
    }
  };

  const [gigSessionName, setGigSessionName] = useState('');
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [showClearPlayedDialog, setShowClearPlayedDialog] = useState(false);

  const clearPlayedSongs = async () => {
    setMessage(null);
    setReordering(true);
    try {
      const res = await apiCall('/api/songs/played', {
        method: 'DELETE'
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to clear played songs');
      }
      const data = await res.json();
      setMessage(`Archived ${data.archived || 0} played song(s)! Votes and history are preserved.`);
      setShowClearPlayedDialog(false);
      fetchPlayedSongs();
    } catch (err: any) {
      setMessage(err.message || 'Error clearing played songs');
    } finally {
      setReordering(false);
    }
  };

  const clearQueue = async () => {
    setMessage(null);
    setReordering(true);
    try {
      const res = await apiCall('/api/songs/queue/expire-all', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gig_session: gigSessionName || null })
      });
      if (!res.ok) throw new Error('Failed to clear queue');
      setMessage(`Queue cleared!${gigSessionName ? ` Saved as gig: "${gigSessionName}"` : ''}`);
      setGigSessionName('');
      setShowClearDialog(false);
      fetchQueue();
    } catch (err: any) {
      setMessage(err.message || 'Error clearing queue');
    } finally {
      setReordering(false);
    }
  };

  const isSuccess = (msg: string) =>
    msg.includes('updated') || msg.includes('removed') || msg.includes('complete') ||
    msg.includes('cleared') || msg.includes('Archived') || msg.includes('started') ||
    msg.includes('ended') || msg.includes('created') || msg.includes('live') || msg.includes('deleted');

  const btnStyle = (color: string): React.CSSProperties => ({
    padding: '8px 16px',
    background: color,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: reordering ? 'not-allowed' : 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
    fontFamily: "'Roboto Slab', serif"
  });

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '20px' }}>Queue / Admin</h1>

      <CameraRecorder ref={recorderRef} enabled={videoRecordingEnabled} onCameraStateChange={setCameraOn} onRecordingStateChange={setRecording} />

      {videoRecordingEnabled && !cameraOn && queue.some(q => q.video_consent) && (
        <div style={{
          marginBottom: 16,
          padding: '10px 14px',
          border: '1px solid #e74c3c',
          borderRadius: 8,
          backgroundColor: 'rgba(231,76,60,0.15)',
          color: '#fff',
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <span>
            Camera is OFF. {queue.filter(q => q.video_consent).length} performer{queue.filter(q => q.video_consent).length === 1 ? '' : 's'} in the queue opted in to a video — turn the camera on before hitting Start.
          </span>
        </div>
      )}

      {pendingUploads.length > 0 && (
        <div style={{
          marginBottom: 16,
          padding: 12,
          border: '1px solid #e74c3c',
          borderRadius: 8,
          backgroundColor: 'rgba(231,76,60,0.08)',
        }}>
          <div style={{ color: '#f5deb3', fontWeight: 'bold', marginBottom: 8 }}>
            Pending uploads ({pendingUploads.length})
          </div>
          {pendingUploads.map(p => (
            <div key={p.historyId} style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              padding: '6px 0',
              borderTop: '1px dashed #5a3a1a',
              color: '#d4a574',
              fontSize: 13,
            }}>
              <span style={{ flex: 1 }}>
                <strong>{p.userName}</strong> — {p.songTitle} ({Math.round(p.blob.size / 1024 / 1024)} MB
                {p.attempts > 0 ? `, ${p.attempts} failed attempt${p.attempts > 1 ? 's' : ''}` : ''}
                {p.lastError ? ` — ${p.lastError}` : ''})
              </span>
              <button
                onClick={() => attemptUpload(p)}
                style={{
                  padding: '4px 10px',
                  background: 'linear-gradient(180deg, #c8850f 0%, #8b5a0a 100%)',
                  color: '#fff',
                  border: '1px solid #5a3a1a',
                  borderRadius: 4,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Retry
              </button>
              <button
                onClick={async () => {
                  await removePending(p.historyId);
                  await refreshPending();
                }}
                style={{
                  padding: '4px 10px',
                  background: 'transparent',
                  color: '#a08060',
                  border: '1px solid #5a3a1a',
                  borderRadius: 4,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Discard
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Gig Status Banner */}
      <div style={{
        padding: '16px 20px',
        marginBottom: '20px',
        borderRadius: '8px',
        backgroundColor: currentGig ? 'rgba(46,204,113,0.1)' : 'rgba(231,76,60,0.1)',
        border: `1px solid ${currentGig ? 'rgba(46,204,113,0.3)' : 'rgba(231,76,60,0.3)'}`
      }}>
        {currentGig ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <strong style={{ fontSize: '18px', color: '#2ecc71' }}>Active Gig: {currentGig.name}</strong>
              <div style={{ fontSize: '14px', color: '#806040', marginTop: '4px' }}>
                Started: {new Date(currentGig.started_at).toLocaleString()}
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#d4a574', marginTop: 8, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={!!currentGig.video_enabled}
                  onChange={e => toggleVideoEnabled(e.target.checked)}
                  style={{ accentColor: '#c8850f', width: 16, height: 16 }}
                />
                Record performance videos
              </label>
            </div>
            {!showEndGigDialog ? (
              <button onClick={() => setShowEndGigDialog(true)} disabled={reordering} style={btnStyle('#e74c3c')}>
                End Gig
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>End this gig? Queue will be archived.</span>
                <button onClick={endGig} disabled={reordering} style={btnStyle('#e74c3c')}>Confirm End</button>
                <button onClick={() => setShowEndGigDialog(false)} disabled={reordering} style={btnStyle('#5a3a1a')}>Cancel</button>
              </div>
            )}
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: '12px' }}>
              <strong style={{ fontSize: '18px', color: '#e74c3c' }}>No Active Gig</strong>
              <div style={{ fontSize: '14px', color: '#806040', marginTop: '4px' }}>
                Go live with an upcoming gig, or quick-start a new one.
              </div>
            </div>
            {!showStartGigDialog ? (
              <button onClick={() => setShowStartGigDialog(true)} disabled={reordering} style={btnStyle('#2ecc71')}>
                Quick Start Gig
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={newGigName}
                  onChange={(e) => setNewGigName(e.target.value)}
                  placeholder="Enter gig name (e.g., Friday Night at Joe's)"
                  style={{ padding: '10px', fontSize: '14px', minWidth: '280px' }}
                  onKeyPress={(e) => e.key === 'Enter' && startGig()}
                  autoFocus
                />
                <button onClick={startGig} disabled={reordering || !newGigName.trim()} style={btnStyle('#2ecc71')}>Start Gig</button>
                <button onClick={() => { setShowStartGigDialog(false); setNewGigName(''); }} disabled={reordering} style={btnStyle('#5a3a1a')}>Cancel</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upcoming Gigs */}
      <div style={{
        padding: '16px 20px',
        marginBottom: '20px',
        borderRadius: '8px',
        backgroundColor: 'rgba(200,133,15,0.05)',
        border: '1px solid rgba(200,133,15,0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
          <strong style={{ fontSize: '16px', color: '#c8a060' }}>Upcoming Gigs</strong>
          {!showCreateGigForm && (
            <button onClick={() => setShowCreateGigForm(true)} disabled={reordering} style={btnStyle('#c8850f')}>
              + Create Gig
            </button>
          )}
        </div>

        {/* Create Gig Form */}
        {showCreateGigForm && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '12px',
            border: '1px solid #5a3a1a',
            borderRadius: '6px',
            background: '#28190f',
          }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#a08060', marginBottom: '4px' }}>Name *</label>
                <input
                  type="text"
                  value={newGigName}
                  onChange={(e) => setNewGigName(e.target.value)}
                  placeholder="Friday Night at Joe's"
                  style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#1a0f0a', border: '1px solid #5a3a1a', borderRadius: '4px', color: '#f5deb3' }}
                  autoFocus
                />
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#a08060', marginBottom: '4px' }}>Date *</label>
                <input
                  type="datetime-local"
                  value={newGigDate}
                  onChange={(e) => setNewGigDate(e.target.value)}
                  style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#1a0f0a', border: '1px solid #5a3a1a', borderRadius: '4px', color: '#f5deb3' }}
                />
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#a08060', marginBottom: '4px' }}>Venue</label>
                <input
                  type="text"
                  value={newGigVenue}
                  onChange={(e) => setNewGigVenue(e.target.value)}
                  placeholder="The Honky Tonk Bar"
                  style={{ width: '100%', padding: '8px', fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#1a0f0a', border: '1px solid #5a3a1a', borderRadius: '4px', color: '#f5deb3' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={createUpcomingGig} disabled={reordering || !newGigName.trim() || !newGigDate} style={btnStyle('#2ecc71')}>Create</button>
                <button onClick={() => { setShowCreateGigForm(false); setNewGigName(''); setNewGigDate(''); setNewGigVenue(''); setNewGigVideoEnabled(true); }} disabled={reordering} style={btnStyle('#5a3a1a')}>Cancel</button>
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#d4a574', marginTop: 10, cursor: 'pointer', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={newGigVideoEnabled}
                onChange={e => setNewGigVideoEnabled(e.target.checked)}
                style={{ accentColor: '#c8850f', width: 16, height: 16 }}
              />
              Record performance videos (performers opt in per song)
            </label>
          </div>
        )}

        {/* Upcoming Gig List */}
        {upcomingGigs.length === 0 ? (
          <p style={{ color: '#806040', fontStyle: 'italic', margin: 0 }}>No upcoming gigs scheduled</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {upcomingGigs.map(gig => (
              <div key={gig.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                borderRadius: '6px',
                background: '#28190f',
                border: '1px solid #3a2510',
                flexWrap: 'wrap',
                gap: '8px',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 'bold', color: '#f5deb3', fontSize: '15px' }}>{gig.name}</div>
                  <div style={{ fontSize: '13px', color: '#a08060', marginTop: '2px' }}>
                    {gig.scheduled_date ? new Date(gig.scheduled_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'No date'}
                    {gig.venue && <> &bull; {gig.venue}</>}
                    {parseInt(gig.queue_count || '0') > 0 && (
                      <span style={{ color: '#c8850f' }}> &bull; {gig.queue_count} song{parseInt(gig.queue_count || '0') !== 1 ? 's' : ''} requested</span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button onClick={() => goLiveWithGig(gig.id)} disabled={reordering} style={btnStyle('#2ecc71')}>Go Live</button>
                  <button onClick={() => deleteUpcomingGig(gig.id)} disabled={reordering} style={btnStyle('#e74c3c')}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Clear Queue */}
      <div style={{ marginBottom: 16 }}>
        {!showClearDialog ? (
          <button onClick={() => setShowClearDialog(true)} disabled={reordering || !currentGig}>
            Clear Queue
          </button>
        ) : (
          <div style={{
            display: 'inline-block',
            padding: '12px 16px',
            border: '1px solid #5a3a1a',
            borderRadius: '6px',
            background: '#28190f',
            marginBottom: '8px'
          }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#c8a060' }}>
              Are you sure you want to clear the queue?
            </label>
            <input
              type="text"
              value={gigSessionName}
              onChange={(e) => setGigSessionName(e.target.value)}
              placeholder="Note (optional)"
              style={{ padding: '6px 10px', marginRight: '8px', fontSize: '14px', minWidth: '250px' }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') clearQueue();
                else if (e.key === 'Escape') { setShowClearDialog(false); setGigSessionName(''); }
              }}
            />
            <button onClick={clearQueue} disabled={reordering} style={{ marginRight: 8 }}>Confirm Clear</button>
            <button onClick={() => { setShowClearDialog(false); setGigSessionName(''); }} disabled={reordering}>Cancel</button>
          </div>
        )}
      </div>

      {loading && <p style={{ color: '#a08060' }}>Loading queue...</p>}
      {error && <p style={{ color: '#e74c3c' }}>{error}</p>}
      {message && (
        <p style={{
          color: isSuccess(message) ? '#2ecc71' : '#e74c3c',
          padding: '8px 12px',
          background: isSuccess(message) ? 'rgba(46,204,113,0.1)' : 'rgba(231,76,60,0.1)',
          borderRadius: '4px',
          border: `1px solid ${isSuccess(message) ? 'rgba(46,204,113,0.3)' : 'rgba(231,76,60,0.3)'}`
        }}>{message}</p>
      )}

      {hasLocalChanges && (
        <div style={{
          color: '#f39c12',
          backgroundColor: 'rgba(243,156,18,0.1)',
          border: '1px solid rgba(243,156,18,0.3)',
          padding: '12px 16px',
          borderRadius: '4px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span>Unsaved changes</span>
          <button onClick={() => saveOrder()} disabled={reordering} style={btnStyle('#2ecc71')}>Save Order</button>
          <button onClick={() => { setHasLocalChanges(false); fetchQueue(); }} style={btnStyle('#5a3a1a')}>Discard</button>
        </div>
      )}

      {/* Draggable Queue */}
      <div style={{ marginBottom: '40px' }}>
        {activeQueue.length === 0 && !loading && (
          <p style={{ color: '#806040', fontStyle: 'italic' }}>No songs in queue</p>
        )}
        {activeQueue.map((item, idx) => (
          <div
            key={item.id}
            draggable={!reordering}
            onDragStart={() => handleDragStart(idx)}
            onDragEnter={() => handleDragEnter(idx)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => e.preventDefault()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              marginBottom: '4px',
              borderRadius: '6px',
              background: item.status === 'playing'
                ? 'rgba(255,215,0,0.08)'
                : dragIndex === idx
                  ? 'rgba(200,133,15,0.15)'
                  : 'rgba(40,25,15,0.95)',
              border: item.status === 'playing'
                ? '1px solid rgba(255,215,0,0.3)'
                : '1px solid #3a2510',
              cursor: reordering ? 'default' : 'grab',
              opacity: dragIndex === idx ? 0.5 : 1,
              transition: 'background 0.15s, opacity 0.15s',
              userSelect: 'none' as const
            }}
          >
            {/* Order + move buttons — two side-by-side buttons for easy touch use (iPad),
                with the queue position above them. Keeps the card the same height. */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: '74px' }}>
              <span style={{ color: '#806040', fontSize: '12px', fontWeight: 'bold', lineHeight: 1 }}>{idx + 1}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => moveItem(idx, idx - 1)}
                  disabled={idx === 0 || reordering}
                  aria-label="Move up"
                  style={{
                    width: '32px', height: '30px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: (idx === 0 || reordering) ? 'transparent' : 'rgba(200,133,15,0.15)',
                    border: `1px solid ${(idx === 0 || reordering) ? '#3a2510' : 'rgba(200,133,15,0.45)'}`,
                    borderRadius: '5px',
                    color: idx === 0 ? '#3a2510' : '#c8850f',
                    cursor: (idx === 0 || reordering) ? 'default' : 'pointer',
                    fontSize: '16px', lineHeight: 1, padding: 0, touchAction: 'manipulation',
                  }}
                >
                  ▲
                </button>
                <button
                  onClick={() => moveItem(idx, idx + 1)}
                  disabled={idx === activeQueue.length - 1 || reordering}
                  aria-label="Move down"
                  style={{
                    width: '32px', height: '30px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: (idx === activeQueue.length - 1 || reordering) ? 'transparent' : 'rgba(200,133,15,0.15)',
                    border: `1px solid ${(idx === activeQueue.length - 1 || reordering) ? '#3a2510' : 'rgba(200,133,15,0.45)'}`,
                    borderRadius: '5px',
                    color: idx === activeQueue.length - 1 ? '#3a2510' : '#c8850f',
                    cursor: (idx === activeQueue.length - 1 || reordering) ? 'default' : 'pointer',
                    fontSize: '16px', lineHeight: 1, padding: 0, touchAction: 'manipulation',
                  }}
                >
                  ▼
                </button>
              </div>
            </div>

            {/* Song info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontWeight: 'bold',
                color: item.status === 'playing' ? '#ffd700' : '#f5deb3',
                fontSize: '15px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {item.title}
              </div>
              <div style={{ color: '#a08060', fontSize: '13px' }}>
                {item.artist} — <span style={{ color: '#c8850f' }}>{item.user_name}</span>
              </div>
            </div>

            {/* Video status badge — only shown when the gig has video enabled */}
            {videoRecordingEnabled && item.video_consent && (
              item.status === 'playing' ? (
                recording ? (
                  <span title="Camera is rolling for this performance" style={{
                    padding: '3px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    color: '#fff',
                    background: 'rgba(231,76,60,0.85)',
                    border: '1px solid rgba(231,76,60,1)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}>
                    <span style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#fff',
                      boxShadow: '0 0 6px #fff',
                    }} />
                    Recording
                  </span>
                ) : (
                  <span title="Performer opted in but the camera isn't recording" style={{
                    padding: '3px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    color: '#e74c3c',
                    background: 'rgba(231,76,60,0.15)',
                    border: '1px solid rgba(231,76,60,0.5)',
                  }}>
                    Not recording
                  </span>
                )
              ) : (
                <span title="Performer opted in to a video of this song" style={{
                  padding: '3px 8px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  color: '#c8850f',
                  background: 'rgba(200,133,15,0.12)',
                  border: '1px solid rgba(200,133,15,0.4)',
                }}>
                  📹 Video
                </span>
              )
            )}

            {/* Status badge */}
            {item.status === 'playing' && (
              <span style={{
                padding: '3px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 'bold',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: '#ffd700',
                background: 'rgba(255,215,0,0.15)',
                border: '1px solid rgba(255,215,0,0.3)'
              }}>
                Playing
              </span>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              {item.status !== 'playing' && (
                <button
                  onClick={() => setCurrentSong(item.id)}
                  disabled={reordering}
                  style={{
                    ...btnStyle('#c8850f'),
                    padding: '6px 12px',
                    fontSize: '12px'
                  }}
                >
                  Start
                </button>
              )}
              {item.status === 'playing' && (
                <>
                  <button
                    onClick={() => unstartSong(item.id)}
                    disabled={reordering}
                    style={{
                      ...btnStyle('#c8850f'),
                      padding: '6px 12px',
                      fontSize: '12px'
                    }}
                  >
                    Stop
                  </button>
                  <button
                    onClick={() => markAsComplete(item.id)}
                    disabled={reordering}
                    style={{
                      ...btnStyle('#2ecc71'),
                      padding: '6px 12px',
                      fontSize: '12px'
                    }}
                  >
                    Done
                  </button>
                </>
              )}
              <button
                onClick={() => removeItem(item.id)}
                disabled={reordering}
                style={{
                  ...btnStyle('#e74c3c'),
                  padding: '6px 12px',
                  fontSize: '12px'
                }}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Played Songs Section */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
          <h2 style={{ margin: 0 }}>Recently Played</h2>
          {playedSongs.length > 0 ? (
            <>
              {!showClearPlayedDialog ? (
                <button onClick={() => setShowClearPlayedDialog(true)} disabled={reordering} style={btnStyle('#e74c3c')}>
                  Archive Played Songs
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ color: '#e74c3c', fontWeight: 'bold', fontSize: '13px' }}>
                    Archive all? Votes preserved.
                  </span>
                  <button onClick={clearPlayedSongs} disabled={reordering} style={btnStyle('#e74c3c')}>Confirm</button>
                  <button onClick={() => setShowClearPlayedDialog(false)} disabled={reordering} style={btnStyle('#5a3a1a')}>Cancel</button>
                </div>
              )}
            </>
          ) : null}
        </div>
        {playedSongs.length === 0 ? (
          <p style={{ color: '#806040', fontStyle: 'italic' }}>No songs have been played yet</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Artist</th>
                  <th>Performer</th>
                  <th style={{ textAlign: 'center' }}>Rating</th>
                  <th style={{ textAlign: 'center' }}>Votes</th>
                  <th style={{ textAlign: 'center' }}>Video</th>
                  <th>Completed</th>
                </tr>
              </thead>
              <tbody>
                {playedSongs.map((song) => (
                  <tr key={song.history_id}>
                    <td style={{ color: '#f5deb3' }}>{song.title}</td>
                    <td style={{ color: '#a08060' }}>{song.artist}</td>
                    <td style={{ color: '#c8850f' }}>{song.user_name}</td>
                    <td style={{ textAlign: 'center' }}>
                      {song.average_rating > 0 ? (
                        <span style={{ color: '#ffd700', fontSize: '15px' }}>
                          {'★'.repeat(Math.round(song.average_rating))}
                          <span style={{ color: '#5a3a1a' }}>{'★'.repeat(5 - Math.round(song.average_rating))}</span>
                        </span>
                      ) : (
                        <span style={{ color: '#5a3a1a' }}>No ratings</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center', color: '#a08060' }}>{song.vote_count}</td>
                    <td style={{ textAlign: 'center', fontSize: 12 }}>{renderVideoCell(song)}</td>
                    <td style={{ color: '#806040', fontSize: '13px' }}>
                      {song.completed_at ? new Date(song.completed_at).toLocaleString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default QueueAdminPage;
